import { insertString } from "common/utils/strings";

/**
 * Given a string, returns all tokens than could map
 * to a possible message template variable
 */
export const templateVariableTokens = (body: string) => {
  const matches = body.matchAll(/{{ ([\w]+) }}/g);
  const tokens = [...matches].map(([, variable]) => variable);
  return new Set<string>(tokens);
};

/**
 * Inserts the template variable in the template body, adding spaces
 * to maintain wordbreaks around inserted variable
 * @param body The message template
 * @param variable The variable name (eg. 'first_name')
 * @param start The index to start insert (inclusive), defaults to the end
 *     of the template
 * @param end The index to end insert (exclusive), all characters between
 *     `start` and `end` will be replaced, defaults to start,
 *     meaining no characters will be replaced
 */
export const insertTemplateVariable = (
  body: string,
  variable: string,
  start: number = body.length,
  end: number = start,
) => {
  const templatedVariable = `{{ ${variable} }}`;

  const isWordOrVariableChar = (char: string) => ["{", "}"].includes(char) || !!char.match(/\w/);
  const shouldPrependSpace = start > 0 && isWordOrVariableChar(body[start - 1]);
  const shouldAppendSpace = end < body.length && isWordOrVariableChar(body[end]);

  const { value, inserted } = insertString({
    current: body,
    insert: templatedVariable,
    start,
    end,
    shouldPrependSpace,
    shouldAppendSpace,
  });
  return { body: value, inserted };
};

/**
 * Utility function to construct a text message by populating the
 * template string with the appropriate variables
 * */
export const compileTemplateBody = <T extends Record<string, string>>(
  body: string,
  data: T,
  { report_missing = false }: { report_missing?: boolean } = {},
) => {
  // Start out with the preview set to the template string
  let compiledBody = body;
  const tokens = templateVariableTokens(body);
  Array.from(tokens).forEach((token) => {
    if (token in data) {
      const variable = `{{ ${token} }}`;
      const regex = new RegExp(variable, "g");
      const value = data[token as keyof T] || (report_missing ? `{{ Missing - ${token} }}` : "");
      compiledBody = compiledBody.replace(regex, value.toString());
    }
  });
  return compiledBody;
};
